/**
 * SecretModal Component
 *
 * Modal for viewing, creating, or editing organization secrets.
 * Supports Valor Paytech credentials with form validation.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useSecret, useSaveSecret } from '@/lib/hooks/useSecrets';
import { organizationApi } from '@/lib/pillarApi';
import type {
    CreateSecretResponse,
    GetOrganizationResponse,
    SecretMetadataResponse,
    SecretUuidReference,
} from '@pillar-portal/api-types';
import {
    ThirdPartySystemId,
    CredentialType,
    CredentialTypeDisplayName,
    CredentialTypeRegistry,
    GetOrganizationResponseSchema,
} from '@pillar-portal/api-types';

const log = createComponentLogger('SecretModal');

const ThirdPartySystemIdSchema = z.union([
    z.literal(ThirdPartySystemId.CORE),
    z.literal(ThirdPartySystemId.DEALERTRACK),
    z.literal(ThirdPartySystemId.PCPARTPICKER),
    z.literal(ThirdPartySystemId.VALOR_US),
]);

const secretFormSchema = z.object({
    system_id: ThirdPartySystemIdSchema,
    account_id: z.string().min(1, 'Account ID is required'),
    credential_type: z.enum([CredentialType.PHYSICAL_TERMINAL, CredentialType.VIRTUAL_TERMINAL]),
    credentials: z.object({
        domain: z
            .string()
            .trim()
            .refine(
                (value) =>
                    value.length === 0 || /^https?:\/\/[a-z0-9.-]+(?::[0-9]+)?$/i.test(value),
                'Invalid domain format. Expected URL format: https://hostname:port or https://hostname'
            ),
        appid: z.string().trim(),
        appkey: z.string().trim(),
        epi: z.string().trim(),
        channel: z.string().trim(),
    }),
    applies_to_children: z.boolean(),
    is_active: z.boolean(),
});

type SecretFormData = z.infer<typeof secretFormSchema>;

const getDefaultSecretFormValues = (): SecretFormData => ({
    system_id: ThirdPartySystemId.VALOR_US,
    account_id: '',
    credential_type: CredentialType.VIRTUAL_TERMINAL,
    credentials: {
        domain: '',
        appid: '',
        appkey: '',
        epi: '',
        channel: '',
    },
    applies_to_children: false,
    is_active: true,
});

interface SecretModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    organizationId?: number;
    organizationUuid?: string;
    organizationName: string;
    secret?: SecretUuidReference | undefined;
    mode: 'view' | 'edit' | 'create';
    lockedCredentialType?: CredentialType;
    onSecretSaved?: (savedSecret: CreateSecretResponse) => void;
}

function isCreateSecretResponse(
    response: CreateSecretResponse | SecretMetadataResponse
): response is CreateSecretResponse {
    return 'organization_uuid' in response;
}

function isSaveSecretResponse(
    response: CreateSecretResponse | SecretMetadataResponse
): response is SecretMetadataResponse {
    return 'credential_uuid' in response;
}

export function SecretModal({
    open,
    onOpenChange,
    organizationUuid,
    organizationName,
    secret,
    mode,
    lockedCredentialType,
    onSecretSaved,
}: SecretModalProps) {
    const [isFormMode, setIsFormMode] = useState(mode !== 'view');
    const [postSaveErrorMessage, setPostSaveErrorMessage] = useState<string | null>(null);
    const handledSaveKeyRef = useRef<string | null>(null);
    const validatedOrganizationUuid = useMemo(() => {
        if (organizationUuid === undefined || organizationUuid === null) {
            return null;
        }
        const parseResult = z.string().uuid().safeParse(organizationUuid);
        return parseResult.success ? parseResult.data : null;
    }, [organizationUuid]);
    const { data: organizationData } = useQuery<GetOrganizationResponse>({
        queryKey: ['secret-modal', 'organization', validatedOrganizationUuid],
        queryFn: async () => {
            if (validatedOrganizationUuid === null) {
                throw new Error('Organization UUID is required');
            }
            const response = await organizationApi.getOrganization(validatedOrganizationUuid);
            return GetOrganizationResponseSchema.parse(response);
        },
        enabled: open && mode === 'create' && validatedOrganizationUuid !== null,
        retry: 0,
    });

    const secretIdentityInput =
        secret !== undefined && secret.credential_uuid !== undefined
            ? {
                  credential_uuid: secret.credential_uuid,
              }
            : undefined;
    const secretIdentityForUpdate =
        secret !== undefined && secret.credential_uuid !== undefined
            ? {
                  credential_uuid: secret.credential_uuid,
              }
            : undefined;

    const {
        data: secretWithCredentials,
        isLoading: isLoadingSecret,
        error: loadError,
    } = useSecret(
        mode === 'view' || mode === 'edit' ? secretIdentityInput : undefined
    );

    const saveMutation = useSaveSecret(secretIdentityForUpdate);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm<SecretFormData>({
        resolver: zodResolver(secretFormSchema),
        defaultValues: getDefaultSecretFormValues(),
    });

    const appliesToChildren = watch('applies_to_children');
    const isActive = watch('is_active');
    const watchedCredentials = watch('credentials');
    const selectedSystemId = watch('system_id');

    const requiredFields: string[] = CredentialTypeRegistry[selectedSystemId]?.requiredFields ?? [];
    const missingCredentialFields: string[] = requiredFields.filter((fieldName) => {
        if (fieldName in watchedCredentials) {
            void fieldName;
            // TODO: we have to tighten the typing here
            // eslint-disable-next-line no-restricted-syntax
            const value = watchedCredentials[fieldName as keyof typeof watchedCredentials];
            return value === undefined || value.trim().length === 0;
        }
        return true;
    });

    // Update form when secret data is available and modal is open.
    // Including `open` ensures we rehydrate the form on every reopen,
    // even when React Query returns cached data with stable reference identity.
    useEffect(() => {
        if (!open) {
            return;
        }
        if (secretWithCredentials && (mode === 'view' || mode === 'edit')) {
            const systemIdParseResult = ThirdPartySystemIdSchema.safeParse(
                secretWithCredentials.system_id
            );
            if (!systemIdParseResult.success) {
                log.error(
                    'Invalid system_id returned from secret lookup',
                    systemIdParseResult.error
                );
                throw new Error(
                    `Invalid system_id returned from secret lookup: ${systemIdParseResult.error.message}`
                );
            }
            const validatedSystemId = systemIdParseResult.data;
            reset({
                system_id: validatedSystemId,
                account_id: secretWithCredentials.account_id,
                credential_type: secretWithCredentials.credential_type,
                credentials: {
                    domain: secretWithCredentials.credentials.domain ?? '',
                    appid: secretWithCredentials.credentials.appid ?? '',
                    appkey: secretWithCredentials.credentials.appkey ?? '',
                    epi: secretWithCredentials.credentials.epi ?? '',
                    channel: secretWithCredentials.credentials.channel ?? '',
                },
                applies_to_children: secretWithCredentials.applies_to_children,
                is_active: secretWithCredentials.is_active,
            });
        }
    }, [open, secretWithCredentials, mode, reset]);

    // Reset form mode when modal opens/closes or mode changes
    useEffect(() => {
        setIsFormMode(mode !== 'view');
        if (open && mode === 'create') {
            const defaultValues: SecretFormData = getDefaultSecretFormValues();
            if (lockedCredentialType !== undefined) {
                defaultValues.credential_type = lockedCredentialType;
            }
            reset(defaultValues);
        }
        if (!open) {
            reset(getDefaultSecretFormValues());
            // Don't reset mutation - React Query handles cleanup on unmount
            // Resetting during unmount/close causes React error #185 (updating unmounted component)
        }
    }, [open, mode, reset, lockedCredentialType]);

    // Reset mutation when modal opens to ensure clean state
    // Do this separately to avoid dependency issues
    useEffect(() => {
        if (open) {
            handledSaveKeyRef.current = null;
            saveMutation.reset();
            setPostSaveErrorMessage(null);
        }
        // saveMutation is intentionally excluded to avoid reset loops
    }, [open]);

    // Handle successful save
    useEffect(() => {
        if (!saveMutation.isSuccess || saveMutation.data === undefined) {
            return;
        }
        const response = saveMutation.data;
        const saveKey: string =
            mode === 'create' && isCreateSecretResponse(response)
                ? `${response.organization_uuid}:${response.system_id}:${response.account_id}`
                : isSaveSecretResponse(response)
                  ? `${response.credential_uuid}`
                  : 'unknown';
        if (handledSaveKeyRef.current === saveKey) {
            return;
        }
        handledSaveKeyRef.current = saveKey;
        try {
            if (mode === 'create' && onSecretSaved !== undefined && isCreateSecretResponse(response)) {
                onSecretSaved(response);
            }
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Failed to attach newly created secret';
            log.error('Post-save callback failed', error);
            setPostSaveErrorMessage(message);
            return;
        }
        onOpenChange(false);
    }, [saveMutation.isSuccess, saveMutation.data, onOpenChange, onSecretSaved]);

    const onSubmit = async (data: SecretFormData) => {
        const credentials: Record<string, string> = {};
        const credentialEntries: Array<[keyof SecretFormData['credentials'], string]> = [
            ['domain', data.credentials.domain],
            ['appid', data.credentials.appid],
            ['appkey', data.credentials.appkey],
            ['epi', data.credentials.epi],
            ['channel', data.credentials.channel],
        ];
        for (const [fieldName, fieldValue] of credentialEntries) {
            const trimmedValue: string = fieldValue.trim();
            if (trimmedValue.length > 0) {
                credentials[fieldName] = trimmedValue;
            }
        }

        if (mode === 'create') {
            const resolvedOrganizationUuid: string | undefined =
                validatedOrganizationUuid ?? organizationData?.organization_uuid;
            if (resolvedOrganizationUuid === undefined) {
                setPostSaveErrorMessage(
                    'Unable to create secret: organization UUID is unavailable.'
                );
                return;
            }
            saveMutation.mutate({
                organization_uuid: resolvedOrganizationUuid,
                system_id: data.system_id,
                account_id: data.account_id,
                credential_type: data.credential_type,
                credentials,
                applies_to_children: data.applies_to_children,
            });
        } else if (mode === 'edit') {
            if (secretIdentityForUpdate === undefined) {
                throw new Error('Unable to edit secret: credential_uuid is unavailable.');
            }
            saveMutation.mutate({
                account_id: data.account_id,
                credential_type: data.credential_type,
                credentials,
                applies_to_children: data.applies_to_children,
                is_active: data.is_active,
            });
        }
    };

    const handleSwitchToEdit = () => {
        setIsFormMode(true);
    };

    const handleCancel = (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        // Always close the modal when Cancel is clicked
        onOpenChange(false);
    };

    const handleOpenChange = (open: boolean) => {
        try {
            onOpenChange(open);
        } catch (error) {
            log.error('Error in onOpenChange handler', error);
        }
    };

    // const handleOpenChange = (open: boolean | undefined) => {
    //     // Ensure we only pass boolean values to onOpenChange
    //     // Guard against undefined or non-boolean values that might come from Radix UI
    //     if (typeof open === 'boolean') {
    //         try {
    //             onOpenChange(open);
    //         } catch (error) {
    //             console.error('Error in onOpenChange handler:', error);
    //         }
    //     }
    // };

    const getTitle = () => {
        if (mode === 'create') return 'Create Credentials';
        if (mode === 'edit' || isFormMode) return 'Edit Credentials';
        return 'View Credentials';
    };

    const getDescription = () => {
        return `${mode === 'create' ? 'Add new' : isFormMode ? 'Update' : 'View'} API credentials for ${organizationName}`;
    };

    const isViewOnly = mode === 'view' && !isFormMode;
    const credentialTypeLockedForCreateMode: boolean =
        mode === 'create' && lockedCredentialType !== undefined;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{getTitle()}</DialogTitle>
                    <DialogDescription>{getDescription()}</DialogDescription>
                </DialogHeader>

                {loadError && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {loadError instanceof Error
                                ? loadError.message
                                : 'Failed to load secret details'}
                        </AlertDescription>
                    </Alert>
                )}

                {isLoadingSecret && (mode === 'view' || mode === 'edit') && (
                    <div className="py-8 text-center text-muted-foreground">
                        Loading secret details...
                    </div>
                )}

                {(!isLoadingSecret || mode === 'create') && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            runInBackground(handleSubmit(onSubmit)(e), (err) => {
                                log.error('Error submitting form', err);
                            });
                        }}
                        className="space-y-4"
                    >
                        {/* System Selection (only for create) */}
                        {mode === 'create' && (
                            <div>
                                <Label htmlFor="system_id">Payment System</Label>
                                <select
                                    id="system_id"
                                    {...register('system_id', { valueAsNumber: true })}
                                    className="w-full border rounded px-3 py-2"
                                    disabled={isViewOnly}
                                >
                                    <option value={ThirdPartySystemId.VALOR_US}>
                                        Valor Paytech (US)
                                    </option>
                                </select>
                                {errors.system_id && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.system_id.message}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Account ID */}
                        <div>
                            <Label htmlFor="account_id">Mnemonic ID</Label>
                            <Input
                                id="account_id"
                                {...register('account_id')}
                                placeholder="Identifier for this credential set (e.g., Front Desk, Mobile Clinic, Online Store...)
"
                                disabled={isViewOnly}
                            />
                            {errors.account_id && (
                                <p className="text-red-600 text-sm mt-1">
                                    {errors.account_id.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="credential_type">Credential Type</Label>
                            <select
                                id="credential_type"
                                {...register('credential_type')}
                                className="w-full border rounded px-3 py-2"
                                disabled={isViewOnly || credentialTypeLockedForCreateMode}
                            >
                                <option value={CredentialType.VIRTUAL_TERMINAL}>
                                    {CredentialTypeDisplayName[CredentialType.VIRTUAL_TERMINAL]}
                                </option>
                                <option value={CredentialType.PHYSICAL_TERMINAL}>
                                    {CredentialTypeDisplayName[CredentialType.PHYSICAL_TERMINAL]}
                                </option>
                            </select>
                            {errors.credential_type && (
                                <p className="text-red-600 text-sm mt-1">
                                    {errors.credential_type.message}
                                </p>
                            )}
                        </div>

                        {/* Valor Credentials */}
                        <div className="space-y-4 border-t pt-4">
                            <h4 className="font-medium">Valor API Credentials</h4>

                            <div>
                                <Label htmlFor="epi">EPI</Label>
                                <Input
                                    id="epi"
                                    {...register('credentials.epi')}
                                    placeholder="Valor EndPoint identifier"
                                    disabled={isViewOnly}
                                    type="text"
                                />
                                {errors.credentials?.epi && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.credentials.epi.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="appid">App ID</Label>
                                <Input
                                    id="appid"
                                    {...register('credentials.appid')}
                                    placeholder="Valor appid"
                                    disabled={isViewOnly}
                                    type="text"
                                />
                                {errors.credentials?.appid && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.credentials.appid.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="appkey">App Key</Label>
                                <Input
                                    id="appkey"
                                    {...register('credentials.appkey')}
                                    placeholder="Valor appkey"
                                    disabled={isViewOnly}
                                    type="text"
                                />
                                {errors.credentials?.appkey && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.credentials.appkey.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="domain">Domain</Label>
                                <Input
                                    id="domain"
                                    {...register('credentials.domain')}
                                    placeholder="Valor API Root URL"
                                    disabled={isViewOnly}
                                    type="text"
                                />
                                {errors.credentials?.domain && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.credentials.domain.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="channel">Channel</Label>
                                <Input
                                    id="channel"
                                    {...register('credentials.channel')}
                                    placeholder="Valor Channel ID"
                                    disabled={isViewOnly}
                                />
                                {errors.credentials?.channel && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.credentials.channel.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Settings */}
                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="applies_to_children"
                                    checked={appliesToChildren}
                                    onCheckedChange={(checked) =>
                                        setValue('applies_to_children', checked === true)
                                    }
                                    disabled={isViewOnly}
                                />
                                <Label htmlFor="applies_to_children" className="font-normal">
                                    Apply to child organizations
                                </Label>
                            </div>
                            <p className="text-sm text-muted-foreground ml-6">
                                Subsidiary merchants can inherit and use this secret
                            </p>

                            {mode === 'edit' && (
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="is_active"
                                        checked={isActive}
                                        onCheckedChange={(checked) =>
                                            setValue('is_active', checked === true)
                                        }
                                        disabled={isViewOnly}
                                    />
                                    <Label htmlFor="is_active" className="font-normal">
                                        Active
                                    </Label>
                                </div>
                            )}
                        </div>

                        {saveMutation.error && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {saveMutation.error instanceof Error
                                        ? saveMutation.error.message
                                        : 'Failed to save secret'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {postSaveErrorMessage !== null && (
                            <Alert variant="destructive">
                                <AlertDescription>{postSaveErrorMessage}</AlertDescription>
                            </Alert>
                        )}

                        <DialogFooter>
                            {missingCredentialFields.length > 0 && !isViewOnly && (
                                <div className="text-sm text-muted-foreground mr-auto pr-4">
                                    Missing fields:{' '}
                                    <span className="font-medium">
                                        {missingCredentialFields.join(', ')}
                                    </span>
                                    . Save now to keep this as an inactive draft.
                                </div>
                            )}
                            {isViewOnly ? (
                                <>
                                    <Button type="button" variant="outline" onClick={handleCancel}>
                                        Close
                                    </Button>
                                    <Button type="button" onClick={handleSwitchToEdit}>
                                        Edit
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                        disabled={saveMutation.isPending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={saveMutation.isPending}>
                                        {saveMutation.isPending ? 'Saving...' : 'Save'}
                                    </Button>
                                </>
                            )}
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
